/**
 * ImpersonationBanner
 *
 * Persistent banner shown when super admin is impersonating another user.
 * Displays who is being impersonated and an Exit button to restore the
 * original super admin session.
 *
 * Rendered into document.body via a React portal so it overlays every
 * route/view without requiring each page to wrap children.
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { UserCog, LogOut } from 'lucide-react';

function ImpersonationBanner({ originalUser, impersonatedUser, onStopImpersonation }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!originalUser || !impersonatedUser || !mounted) return null;

  const banner = (
    <div
      data-testid="impersonation-banner"
      className="bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm sm:text-base">
          <UserCog className="w-5 h-5" />
          <span className="font-semibold">Impersonation Active:</span>
          <span>
            You are viewing as <strong>{impersonatedUser.name || impersonatedUser.email}</strong>
            {impersonatedUser.role ? <span className="opacity-80"> ({impersonatedUser.role.replace('_', ' ')})</span> : null}
            {impersonatedUser.storeName ? <span className="opacity-80"> · {impersonatedUser.storeName}</span> : null}
          </span>
          <span className="opacity-70 hidden sm:inline">— signed in as {originalUser.email}</span>
        </div>
        <button
          data-testid="stop-impersonation-btn"
          onClick={onStopImpersonation}
          className="flex items-center gap-2 bg-amber-950 text-amber-100 hover:bg-black px-3 py-1.5 rounded-md text-sm font-medium transition"
        >
          <LogOut className="w-4 h-4" />
          Exit Impersonation
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(banner, document.body);
}

export default ImpersonationBanner;
